import React, { useEffect, useRef, useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, useLanguage } from '../../contexts/LanguageContext';

export function LanguageSwitcher({ variant = 'light', dataTestPrefix = 'marketing-language' }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  const active = SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];
  const isDark = variant === 'dark';

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${dataTestPrefix}-switcher-button`}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          borderRadius: 9999,
          border: isDark ? '1px solid rgba(255,255,255,0.18)' : '1px solid var(--mkt-header-border)',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'transparent',
          color: isDark ? '#FFFFFF' : 'var(--mkt-text)',
          fontFamily: "'Manrope', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background-color 160ms ease, border-color 160ms ease',
        }}
      >
        <Globe size={14} strokeWidth={2.2} />
        <span>{active.short}</span>
        <ChevronDown
          size={14}
          strokeWidth={2.2}
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 180ms ease',
          }}
        />
      </button>

      {open ? (
        <ul
          role="listbox"
          data-testid={`${dataTestPrefix}-switcher-menu`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: 90,
            minWidth: 160,
            listStyle: 'none',
            margin: 0,
            padding: 6,
            background: isDark ? '#161127' : '#FFFFFF',
            border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid var(--mkt-card-border)',
            borderRadius: 14,
            boxShadow: '0 20px 40px rgba(15,10,30,0.18)',
          }}
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isActive = lang.code === language;
            return (
              <li key={lang.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLanguage(lang.code);
                    setOpen(false);
                  }}
                  data-testid={`${dataTestPrefix}-switcher-option-${lang.code}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 12px',
                    background: isActive
                      ? (isDark ? 'rgba(229,57,53,0.16)' : 'rgba(229,57,53,0.08)')
                      : 'transparent',
                    color: isDark ? '#FFFFFF' : 'var(--mkt-text)',
                    border: 'none',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  <span>{lang.label}</span>
                  {isActive ? <Check size={14} color="#E53935" strokeWidth={2.6} /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
